import { useMemo, useCallback, useReducer } from 'react';
import { ModalSheet } from '@packages/ui/composites';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionExecutionContext, ComposeModalConfig, ComposeModalMode } from '@packages/table-builder';
import { ControlRenderer, useControlRendererRef } from '@packages/compose/core';

const INTENT_TO_FOOTER: Record<string, ComposeModalMode> = {
  edit: 'view',
  save: 'edit',
  add: 'add',
  approve: 'approver',
};

function resolveMode(meta: ComposeModalConfig | undefined, primaryIntent?: string): ComposeModalMode {
  if (meta?.mode) return meta.mode;
  if (meta?.footerMode) return meta.footerMode;
  if (primaryIntent == null) return 'cancel-only';
  return INTENT_TO_FOOTER[primaryIntent] ?? 'view';
}

function resolveTitle(meta: ComposeModalConfig | undefined, currentMode: ComposeModalMode): string {
  if (!meta) return '';
  if (meta.entity) {
    const modeLabel = currentMode.charAt(0).toUpperCase() + currentMode.slice(1);
    return `${modeLabel} ${meta.entity}`;
  }
  return meta.title ?? '';
}

// --- In-place mode switch reducer (Strategy B) ---
type ModalModeState = { mode: ComposeModalMode };
type ModalModeAction =
  | { type: 'SWITCH_TO_EDIT' }
  | { type: 'RESET'; mode: ComposeModalMode };

function modalModeReducer(state: ModalModeState, action: ModalModeAction): ModalModeState {
  switch (action.type) {
    case 'SWITCH_TO_EDIT':
      return { mode: 'edit' };
    case 'RESET':
      return { mode: action.mode };
  }
}

export interface ComposeModalProps {
  actionId: string;
  context: ActionExecutionContext;
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
}

export function ComposeModal({ actionId, context, isOpen, onClose, onExitComplete }: ComposeModalProps) {
  const meta = context.meta as unknown as ComposeModalConfig | undefined;
  const controlRef = useControlRendererRef();

  const actions = meta?.actions;
  const primaryAction = actions?.[0];
  const secondaryAction = actions?.[1];

  const initialMode = resolveMode(meta, primaryAction?.intent);
  const [modeState, dispatchMode] = useReducer(modalModeReducer, { mode: initialMode });
  const currentMode = modeState.mode;

  const params = useMemo(() => ({ id: context.pk }), [context.pk]);

  // Derive a new meta object when mode switches in-place — triggers ActionBlockRenderer re-execution
  const effectiveMeta = useMemo((): Record<string, unknown> | undefined => {
    if (!meta) return undefined;
    if (currentMode === initialMode) return meta as unknown as Record<string, unknown>;
    return { ...(meta as unknown as Record<string, unknown>), mode: currentMode };
  }, [meta, currentMode, initialMode]);

  const isFormModal = controlRef.formId != null;

  const emitAction = useCallback((modalActionId: string) => {
    onClose();
    void ActionRegistry.executeAction(modalActionId, context);
  }, [onClose, context]);

  // In-place mode switch: view → edit (no close/reopen)
  const handleViewToEdit = useCallback(() => {
    dispatchMode({ type: 'SWITCH_TO_EDIT' });
  }, []);

  const handleAction = useCallback(() => {
    if (!primaryAction) return;

    if (currentMode === 'view' && primaryAction.intent === 'edit') {
      if (isFormModal) {
        handleViewToEdit();
        return;
      }
      emitAction(primaryAction.id);
      return;
    }

    emitAction(primaryAction.id);
  }, [primaryAction, currentMode, isFormModal, handleViewToEdit, emitAction]);

  const handleSecondaryAction = useCallback(() => {
    if (secondaryAction) emitAction(secondaryAction.id);
  }, [secondaryAction, emitAction]);

  return (
    <ModalSheet
      open={isOpen}
      onOpenChange={(o) => !o && onClose()}
      onExitComplete={onExitComplete}
      title={resolveTitle(meta, currentMode)}
      subtitle={meta?.subtitle}
      size={(meta?.size ?? 'xl') as any}
      semanticId={meta?.semanticId ?? `${actionId}Modal`}
      loading={controlRef.isLoading}
      mode={currentMode}
      // Form footer wiring — only active when in edit/add mode AND the block is a form
      actionForm={isFormModal && currentMode !== 'view' ? controlRef.formId : undefined}
      dirty={isFormModal && currentMode !== 'view' ? controlRef.isDirty : undefined}
      actionLoading={isFormModal && currentMode !== 'view' ? controlRef.isSubmitting : undefined}
      actionDisabled={isFormModal && currentMode === 'edit' ? !controlRef.isDirty : undefined}
      actionLabel={currentMode === 'view' ? 'Edit' : (meta?.actionLabel ?? (!isFormModal ? primaryAction?.label : undefined))}
      cancelLabel={secondaryAction?.label}
      onAction={currentMode === 'view' ? handleAction : (!isFormModal ? handleAction : undefined)}
      onSecondaryAction={secondaryAction ? handleSecondaryAction : undefined}>
      {isOpen && (
        <ControlRenderer
          key={context.pk}
          actionId={actionId}
          params={params}
          meta={effectiveMeta}
          handle={controlRef}
          onSuccess={onClose}
        />
      )}
    </ModalSheet>
  );
}
